import { createContext, useContext, useEffect, useState } from 'react';

import { CustomEvents, SpecialAnchorTypes, ToolBaseTypes, ToolTypes } from '@labradorsports/constants';

import { FieldPiece, Move } from '../models/index.js';

import { usePlayback } from './playback.js';
import { usePlayEditor } from './playEditor.js';

export interface SelectedContextValue {
    selected: FieldPiece;
    setSelected: (selected: FieldPiece) => void;
    hovering: FieldPiece;
    setHoveringPiece: (hovering: FieldPiece) => void;
    setHoveringMove: (hovering: Move) => void;
    editPiece: FieldPiece;
    setEditPiece: (selected: FieldPiece) => void;
}

export const SelectedContext = createContext<SelectedContextValue>(null);

export const useSelected = (): SelectedContextValue => {
    return useContext(SelectedContext);
};

export const SelectedProvider: FC = ({ children }) => {
    const [selected, _setSelected] = useState<FieldPiece>(null);
    // hoveringPiece and hoveringMove are both needed to avoid piece enter/leave events clobbering hover state
    // when near-line detection is still considered a hover
    const [hoveringPiece, _setHoveringPiece] = useState<FieldPiece>(null);
    const [hoveringMove, _setHoveringMove] = useState<Move>(null);
    const [editPiece, setEditPiece] = useState<FieldPiece>(null);
    const { currentFrame, playData, currentFrameIdx, PlayConfig, readOnly, fieldSetup } = usePlayEditor();
    const { playing } = usePlayback();

    const canHoverPiece = (piece: FieldPiece) => {
        if (fieldSetup && piece instanceof Move) {
            return false;
        }

        return true;
    };

    const setHoveringPiece = (hovering: FieldPiece) => {
        if (canHoverPiece(hovering)) {
            _setHoveringPiece(hovering);
        }
    };

    const setHoveringMove = (hovering: Move) => {
        if (canHoverPiece(hovering)) {
            _setHoveringMove(hovering);
        }
    };

    const clearSelection = () => {
        _setSelected(null);
        setHoveringPiece(null);
        setHoveringMove(null);
    };

    const setSelected = (selected: FieldPiece) => {
        clearSelection();
        _setSelected(selected);
    };

    // Only maintain selection if we did not switch to/from field setup
    useEffect(() => {
        clearSelection();
    }, [playData?.fieldSetup, playData?.id, currentFrameIdx, playing]);

    useEffect(() => {
        if (selected && currentFrame) {
            const newSelected = (() => {
                if ([ToolTypes.BLUEPLAYER, ToolTypes.ORANGEPLAYER].includes(selected.type)) {
                    return currentFrame.getPlayer(selected.id);
                }

                if (readOnly) {
                    return null;
                }

                // All options here use optional chaining, because the selected item could be removed by an undo
                if (selected instanceof FieldPiece) {
                    if (selected.type === ToolTypes.BALL) {
                        return currentFrame.getBall(selected.id);
                    }

                    if (
                        [
                            ToolTypes.OLINE,
                            ToolTypes.DLINE,
                            ToolTypes.GOAL,
                            ToolTypes.RECTANGLE,
                            ToolTypes.ELLIPSE,
                            SpecialAnchorTypes.LINE_ROTATE,
                            SpecialAnchorTypes.SHADING_RESIZE,
                            SpecialAnchorTypes.SHADING_ROTATE,
                        ].includes(selected.type)
                    ) {
                        return currentFrame.field.getFieldPiece(selected.id);
                    }
                }

                if (selected instanceof Move) {
                    if (selected.type === ToolTypes.OPTION) {
                        return currentFrame.options?.find((opt) => opt.target === selected.target);
                    }

                    if (selected.type === ToolTypes.ROUTE_OPTION) {
                        return currentFrame.routeOptions?.find((ro) => ro.id === selected.id);
                    }

                    if (PlayConfig.MoveConfig[selected.type]?.baseType === ToolBaseTypes.PASS) {
                        return currentFrame.passes?.find((pass) => pass.id === selected.id);
                    }

                    if (PlayConfig.MoveConfig[selected.type]?.baseType === ToolBaseTypes.SHOOT) {
                        return currentFrame.shots?.find((shot) => shot.id === selected.id);
                    }

                    if (selected.type === ToolTypes.ASSIGN) {
                        return currentFrame.assignments?.find(
                            (asn: any) => asn.target === selected.target && asn.source === selected.source
                        );
                    }

                    if (selected.type === ToolTypes.COMMENT) {
                        return currentFrame.fieldComments?.find((comment) => comment.id === selected.id);
                    }

                    return currentFrame.moves?.find((mv: any) => mv.target === selected.target);
                }

                return null;
            })();

            if (newSelected) {
                if (newSelected !== selected) {
                    setSelected(newSelected);
                }
            } else {
                setSelected(null);
            }
        }
    }, [currentFrame, selected]);

    const value: SelectedContextValue = {
        selected,
        setSelected,
        hovering: hoveringPiece || hoveringMove,
        setHoveringPiece,
        setHoveringMove,
        editPiece,
        setEditPiece,
    };

    return <SelectedContext.Provider value={value}>{children}</SelectedContext.Provider>;
};
